/**
 * 모듈 그리드/인벤토리 커맨드 (일감 S2c 전반부) — GameScene grantModule/place/move/remove/unlock/upgrade 추출 (Phaser-free).
 * 도메인 결정 + ShellPorts 부수효과만 — 뷰 갱신은 invalidateUi → TabRefresher 경로,
 * 이동 실패 스냅백(드래그 타일 재렌더)만 셸 콜백.
 * 컨트롤러는 ctx getter 경유 (applySaveData 교체 stale 방지 규약).
 */
import { EconomyController } from "../../systems/EconomyController.js";
import {
  createModuleInstance,
  effectiveRarity,
  type ModuleInstance,
} from "../../models/ModuleInstance.js";
import { planRecommendedEquip, type EquipCandidate } from "../../systems/recommendEquip.js";
import {
  pickPiece,
  type ModulePiece,
  type PieceRandomSource,
} from "../../data/modulePieces.js";
import type { ModuleRarity } from "../../modules/ProjectileModule.js";
import type { GameContext } from "../GameContext.js";
import type { ShellPorts } from "./ports.js";
import { AnalyticsEvent } from "../../analytics/analyticsEvents.js";
import { synergyDefinitionOf } from "../../data/moduleSynergyDefinitions.js";
import { BigNumber } from "@yjsgames/bignumber";
import { resolvePurchase, type Multiplier } from "../../systems/multiBuy.js";

export interface GridCommandDeps {
  ctx: GameContext;
  ports: ShellPorts;
  /** 도감 발견 기록 — 셸 소유 Set (readonly 필드, 로드 시 clear+add 유지). */
  discovered: () => Set<string>;
  /** 조각 추첨 난수원 — 셸 필드 getter (테스트/치트 교체 대응). */
  pieceRng: () => PieceRandomSource;
  /** 이동 실패 시 그리드 재렌더 스냅백 — 드래그로 떠 있던 타일을 원위치에 다시 그린다. */
  snapbackBattleGrid: () => void;
  /**
   * 배치 성공 시 알림 (선택) — 튜토리얼 place 스텝(advance=action, actionKey=module.placed) 진행용.
   * 드래그 배치·AutoPlay PLACE 둘 다 place()를 거치므로 단일 결선점. 실패(canPlace false) 시 미발화.
   */
  onPlaced?: (definitionId: string) => void;
}

export class GridCommands {
  constructor(private readonly deps: GridCommandDeps) {}

  /**
   * 정의 1종을 새 인스턴스로 지급(배치는 플레이어). 도감 발견 기록. 반환: 생성 인스턴스(없는 정의면 null).
   * 조각(shape+footprint)은 정의 조각 풀(def.pieces)에서 랜덤 추첨. pieceOverride 지정 시 그 조각으로 고정(튜토리얼 스타터 등 배치 보장용).
   */
  grantModule(
    definitionId: string,
    pieceOverride?: ModulePiece,
    rarityOverride?: ModuleRarity,
  ): ModuleInstance | null {
    const { ctx, ports } = this.deps;
    const def = ctx.moduleRegistry().getDefinition(definitionId);
    if (!def) return null;
    // 조각 추첨 시 현재 최고 도달 챕터(bestChapter, 환생 유지)로 해금 필터 — 초반엔 1칸만, 멀티셀은 높은 챕터부터.
    // pieceOverride(튜토리얼 스타터)는 배치 보장용이라 필터 우회.
    const piece =
      pieceOverride ?? pickPiece(this.deps.pieceRng(), def.pieces, ctx.chapter().bestChapter);
    const inst = ctx.inventory().addInstance(
      createModuleInstance({
        definitionId,
        instanceId: ctx.idSource()(),
        acquiredAt: Date.now(),
        shapeId: piece.shapeId,
        footprintId: piece.footprintId,
        // 획득 등급 추첨 결과 — 미지정 시 createModuleInstance가 키를 생략해 정의 기본 등급(effectiveRarity)을 따른다.
        rarity: rarityOverride,
      }),
    );
    this.deps.discovered().add(definitionId);
    ctx.soundController().play("acquire");
    ports.invalidateUi();
    return inst;
  }

  /** 그리드에 인스턴스 배치 (보유 인스턴스만). 인스턴스가 고정 보유한 조각(shapeId+footprintId)으로 배치. */
  place(x: number, y: number, instanceId: string): boolean {
    const { ctx, ports } = this.deps;
    const inst = ctx.inventory().getInstance(instanceId);
    if (!inst) return false;
    if (ctx.grid().placementOf(instanceId)) return false;
    if (!ctx.grid().place(x, y, instanceId, inst.definitionId, inst.shapeId, inst.footprintId)) {
      return false;
    }
    ports.invalidateUi();
    ports.persist();
    ports.resetFireTimer();
    this.deps.onPlaced?.(inst.definitionId); // 튜토리얼/가이드퀘스트 place 스텝 진행 (배치 성공 시에만)
    return true;
  }

  /** 그리드 내 이동 — 실패(대상 점유/범위 밖/footprint 불일치) 시 스냅백 재렌더. */
  move(fromX: number, fromY: number, toX: number, toY: number): void {
    const { ctx, ports } = this.deps;
    if (!ctx.grid().move(fromX, fromY, toX, toY)) {
      // 드래그로 떠 있던 타일 객체를 파괴하고 원위치에 다시 그린다. 누락 시 모듈이 사라진 듯 보임.
      this.deps.snapbackBattleGrid();
      return;
    }
    ports.invalidateUi();
    ports.persist();
    ports.resetFireTimer();
  }

  /** 그리드에서 제거 (인벤토리 보관으로 복귀). */
  removeAt(x: number, y: number): void {
    const { ctx, ports } = this.deps;
    if (!ctx.grid().removeAt(x, y)) return;
    ports.invalidateUi();
    ports.persist();
    ports.resetFireTimer();
  }

  /** 모두 장착 해제 — 배치된 모든 모듈을 인벤토리로 회수(해금 상태 유지). 배치 0건이면 no-op. */
  unequipAll(): void {
    const { ctx, ports } = this.deps;
    if (ctx.grid().placedInstanceIds().length === 0) return;
    ctx.grid().clearPlacedModules();
    ports.invalidateUi();
    ports.persist();
    ports.resetFireTimer();
  }

  /**
   * 추천 장착 — 보유 미배치 모듈을 높은 등급순으로 비어있는 장착 가능 칸에 최대한 채운다(기존 배치 유지).
   * 계획은 순수 함수(planRecommendedEquip)가 산출, 여기서 실 그리드에 커밋 + invalidate/persist. 변경 0건이면 no-op.
   */
  recommendEquip(): void {
    const { ctx, ports } = this.deps;
    const grid = ctx.grid();
    const registry = ctx.moduleRegistry();
    const placed = new Set(grid.placedInstanceIds());
    const candidates: EquipCandidate[] = ctx
      .inventory()
      .allInstances()
      .filter((i) => !placed.has(i.instanceId))
      .map((i) => ({
        instanceId: i.instanceId,
        definitionId: i.definitionId,
        shapeId: i.shapeId,
        footprintId: i.footprintId,
        rarity: effectiveRarity(i, registry.getDefinition(i.definitionId)),
        acquiredAt: i.acquiredAt,
      }));
    const plan = planRecommendedEquip(grid, candidates);
    if (plan.length === 0) return;
    for (const p of plan) {
      const inst = ctx.inventory().getInstance(p.instanceId);
      if (!inst) continue;
      if (grid.place(p.x, p.y, p.instanceId, inst.definitionId, inst.shapeId, inst.footprintId)) {
        this.deps.onPlaced?.(inst.definitionId);
      }
    }
    ctx.soundController().play("acquire");
    ports.invalidateUi();
    ports.persist();
    ports.resetFireTimer();
  }

  /** 잠금 슬롯 해금 — 우주선 내부 모듈 슬롯은 다이아로 구매하고 우주선별 소유 상태로 저장한다. */
  unlockCell(x: number, y: number): void {
    const { ctx, ports } = this.deps;
    if (!ctx.grid().canUnlock(x, y)) return;
    if (!ctx.economy().trySpendDiamond(ctx.grid().unlockCostBig())) return;
    ctx.grid().unlock(x, y);
    ctx.spaceship().setModuleUnlockedCells(ctx.spaceship().selectedId, ctx.grid().toState().unlockedCells);
    ctx.soundController().play("box");
    ctx.analytics().log(AnalyticsEvent.GridUnlock); // 그리드 해금 퍼널 (매 해금, funnel #8)
    ports.invalidateUi();
    ports.persistNow(); // 다이아 소비 + 우주선별 슬롯 소유권 변경 — 즉시 저장(Firestore write-behind dirty 보장)
  }

  /**
   * 모듈 레벨업 — 강화석 충족 시 그 moduleId(definitionId)의 플레이어 레벨 +1 (강화석 전용).
   * 레벨은 플레이어 자산이라 같은 moduleId 인스턴스 전체 성능에 즉시 반영 + 환생 유지. 골드 미소모.
   */
  upgradeModule(moduleId: string): void {
    const { ctx, ports } = this.deps;
    const level = ctx.playerModuleLevels().level(moduleId);
    const stoneCost = EconomyController.upgradeStoneCost(level);
    if (ctx.upgradeInv().universalUpgradeStone < stoneCost) return;
    ctx.upgradeInv().trySpendStone(stoneCost);
    ctx.playerModuleLevels().upgrade(moduleId);
    ctx.soundController().play("levelup");
    ports.invalidateUi();
    ports.persist();
  }

  /** 시너지 레벨업 — 선택 배수만큼 효과 레벨을 올린다. 발견 상태와 무관하게 강화석만 소모한다. */
  upgradeSynergy(synergyId: string, multiplier: Multiplier = 1): void {
    const { ctx, ports } = this.deps;
    if (!synergyDefinitionOf(synergyId)) return;
    const level = ctx.synergyLevels().level(synergyId);
    const { count, totalCost } = resolvePurchase(
      (lv) => BigNumber.fromNumber(EconomyController.upgradeStoneCost(lv)),
      level,
      Number.POSITIVE_INFINITY,
      BigNumber.fromNumber(ctx.upgradeInv().universalUpgradeStone),
      multiplier,
    );
    const stoneCost = totalCost.toNumber();
    if (count <= 0) return;
    if (ctx.upgradeInv().universalUpgradeStone < stoneCost) return;
    if (!ctx.upgradeInv().trySpendStone(stoneCost)) return;
    for (let i = 0; i < count; i++) ctx.synergyLevels().upgrade(synergyId);
    ctx.soundController().play("levelup");
    ports.invalidateUi();
    ports.persist();
  }
}
